<template>
  <div class="relative min-h-screen w-full bg-gradient-to-br from-gray-100 to-blue-50">
    <div class="container mx-auto px-4 sm:px-8 py-8">
      <nav class="text-sm text-gray-600 mb-6 flex">
        <router-link to="/dashboard" class="hover:text-blue-700 font-medium">Dashboard</router-link>
        <span class="mx-1">/</span>
        <span class="text-blue-900 font-semibold">Career Fair</span>
      </nav>

      <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p class="text-xs font-bold uppercase tracking-widest text-six m-0 mb-1">People & Partnerships</p>
          <h1 class="text-3xl font-extrabold text-heading m-0">Career Fair</h1>
          <p class="text-slate-500 mt-2 mb-0">Create a year, design the page, then publish it for the public.</p>
        </div>
        <button
          v-if="hasPermission('career-fair-create')"
          type="button"
          class="inline-flex items-center justify-center px-5 py-3 text-sm font-bold text-white bg-primary border-0 rounded-lg cursor-pointer hover:bg-primary-600"
          @click="showCreate = true"
        >
          New career fair
        </button>
      </div>

      <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div v-for="n in 3" :key="'sk-' + n" class="h-48 bg-white rounded-2xl animate-pulse"></div>
      </div>

      <div v-else-if="!items.length" class="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
        <p class="text-lg font-bold text-heading mb-2">No career fairs yet</p>
        <p class="text-slate-500 mb-6">Start with a year — then design the hero and publish when ready.</p>
        <button
          v-if="hasPermission('career-fair-create')"
          type="button"
          class="px-5 py-3 text-sm font-bold text-white bg-primary border-0 rounded-lg cursor-pointer"
          @click="showCreate = true"
        >
          Create 2026
        </button>
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div
          v-for="item in items"
          :key="item.id"
          class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col"
        >
          <div class="cf-year-card px-6 py-8 text-white">
            <p class="text-xs uppercase tracking-widest text-white text-opacity-70 m-0 mb-2">{{ item.title || 'Career Fair' }}</p>
            <p class="text-5xl font-extrabold m-0 leading-none">{{ item.year }}</p>
            <span
              class="inline-block mt-4 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full"
              :class="item.is_published ? 'bg-green-500 text-white' : 'bg-white text-heading'"
            >
              {{ item.is_published ? 'Published' : 'Draft' }}
            </span>
          </div>
          <div class="p-5 flex flex-wrap gap-2 mt-auto">
            <router-link
              :to="'/career-fair-management/' + item.id"
              class="px-3 py-2 text-xs font-bold text-primary border border-primary rounded-lg no-underline hover:bg-primary hover:text-white"
            >
              Edit
            </router-link>
            <router-link
              :to="'/career-fair/' + item.year + '/preview'"
              target="_blank"
              class="px-3 py-2 text-xs font-bold text-slate-600 border border-gray-300 rounded-lg no-underline hover:border-six"
            >
              Preview
            </router-link>
            <button
              v-if="hasPermission('career-fair-publish')"
              type="button"
              class="px-3 py-2 text-xs font-bold border-0 rounded-lg cursor-pointer"
              :class="item.is_published ? 'bg-gray-100 text-slate-600' : 'bg-green-600 text-white'"
              @click="togglePublish(item)"
            >
              {{ item.is_published ? 'Unpublish' : 'Publish' }}
            </button>
            <button
              v-if="hasPermission('career-fair-delete')"
              type="button"
              class="px-3 py-2 text-xs font-bold text-red-600 bg-transparent border-0 cursor-pointer"
              @click="confirmDelete(item)"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showCreate"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      @click.self="showCreate = false"
    >
      <div class="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <h3 class="text-xl font-bold text-heading m-0 mb-1">New career fair</h3>
        <p class="text-sm text-slate-500 mb-5">Each year has its own preview and public page.</p>
        <label class="block text-sm font-semibold mb-1">Year</label>
        <input v-model="createYear" type="number" min="2000" max="2100" class="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4" placeholder="2026" />
        <label class="block text-sm font-semibold mb-1">Title (optional)</label>
        <input v-model="createTitle" type="text" class="w-full px-4 py-3 border border-gray-300 rounded-lg mb-6" placeholder="IAT Career Fair 2026" />
        <div class="flex justify-end gap-2">
          <button type="button" class="px-4 py-2 text-sm font-semibold bg-gray-100 border-0 rounded-lg cursor-pointer" @click="showCreate = false">Cancel</button>
          <button type="button" class="px-4 py-2 text-sm font-semibold text-white bg-primary border-0 rounded-lg cursor-pointer" :disabled="creating" @click="createFair">
            {{ creating ? 'Creating...' : 'Create' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import { cloneApolloData } from "./graphql";

const LIST_QUERY = gql`
  query CareerFairs($first: Int!, $page: Int!, $search: String) {
    careerFairs(first: $first, page: $page, search: $search) {
      error
      message
      data { id year title slug is_published published_at }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation CreateCareerFair($input: CareerFairInput!) {
    createCareerFair(input: $input) {
      error
      message
      data { id year }
    }
  }
`;

const PUBLISH_MUTATION = gql`
  mutation PublishCareerFair($id: ID!, $publish: Boolean!) {
    publishCareerFair(id: $id, publish: $publish) {
      error
      message
      data { id is_published }
    }
  }
`;

const DELETE_MUTATION = gql`
  mutation DeleteCareerFair($id: ID!) {
    deleteCareerFair(id: $id) {
      error
      message
    }
  }
`;

export default {
  name: "CareerFairManagement",
  data() {
    return {
      loading: false,
      creating: false,
      showCreate: false,
      createYear: new Date().getFullYear() + 1,
      createTitle: "",
      items: [],
    };
  },
  computed: {
    role() {
      return this.$store.getters.role;
    },
  },
  created() {
    this.load();
  },
  methods: {
    hasPermission(name) {
      if (this.role === "superadmin") return true;
      var permissions = this.$store.getters.permissions || "";
      return String(permissions).split(",").indexOf(name) !== -1;
    },
    async load() {
      this.loading = true;
      try {
        var result = await this.$apollo.query({
          query: LIST_QUERY,
          variables: { first: 50, page: 1, search: "" },
          fetchPolicy: "network-only",
        });
        var payload = result.data && result.data.careerFairs;
        if (payload && payload.error) {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Career Fair", text: payload.message });
        } else {
          this.items = cloneApolloData((payload && payload.data) || []);
        }
      } catch (e) {
        if (this.$swal) this.$swal.fire({ icon: "error", title: "Failed", text: "Could not load career fairs." });
      } finally {
        this.loading = false;
      }
    },
    async createFair() {
      this.creating = true;
      try {
        var result = await this.$apollo.mutate({
          mutation: CREATE_MUTATION,
          variables: {
            input: {
              year: parseInt(this.createYear, 10),
              title: this.createTitle || null,
            },
          },
          errorPolicy: "all",
          fetchPolicy: "no-cache",
        });
        var payload = result && result.data && result.data.createCareerFair;
        if (payload && payload.error) {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Could not create", text: payload.message });
        } else if (payload && payload.data) {
          this.showCreate = false;
          this.$router.push("/career-fair-management/" + payload.data.id);
        }
      } catch (e) {
        if (this.$swal) this.$swal.fire({ icon: "error", title: "Could not create", text: "Please try again." });
      } finally {
        this.creating = false;
      }
    },
    async togglePublish(item) {
      var publish = !item.is_published;
      var confirm = await this.$swal.fire({
        title: publish ? "Publish this page?" : "Unpublish this page?",
        text: publish
          ? "The public URL /career-fair/" + item.year + " will go live."
          : "The public URL will no longer be available.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: publish ? "Publish" : "Unpublish",
      });
      if (!confirm.isConfirmed) return;
      try {
        var result = await this.$apollo.mutate({
          mutation: PUBLISH_MUTATION,
          variables: { id: item.id, publish: publish },
          errorPolicy: "all",
          fetchPolicy: "no-cache",
        });
        var payload = result && result.data && result.data.publishCareerFair;
        if (payload && payload.error) {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Failed", text: payload.message });
        } else {
          this.items = (this.items || []).map(function (row) {
            if (String(row.id) !== String(item.id)) return row;
            return Object.assign({}, row, { is_published: publish });
          });
        }
      } catch (e) {
        if (this.$swal) this.$swal.fire({ icon: "error", title: "Failed", text: "Please try again." });
      }
    },
    async confirmDelete(item) {
      var confirm = await this.$swal.fire({
        title: "Delete " + item.year + "?",
        text: "This cannot be undone from the list.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
      });
      if (!confirm.isConfirmed) return;
      try {
        var result = await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: { id: item.id },
          errorPolicy: "all",
          fetchPolicy: "no-cache",
        });
        var payload = result && result.data && result.data.deleteCareerFair;
        if (payload && payload.error) {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Failed", text: payload.message });
        } else {
          this.load();
        }
      } catch (e) {
        if (this.$swal) this.$swal.fire({ icon: "error", title: "Failed", text: "Please try again." });
      }
    },
  },
};
</script>

<style scoped>
.cf-year-card {
  background: linear-gradient(135deg, #212A3E 0%, #394867 55%, #0a4275 100%);
}
</style>
